import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaExclamationTriangle, FaEnvelope, FaCheck, FaSpinner, FaTimes } from 'react-icons/fa';

const EmailVerificationWarning = ({ user, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const navigate = useNavigate();

  // Safety check for user prop
  if (!user) {
    return null;
  }

  const handleResendVerification = async () => {
    setLoading(true);
    setMessage('');
    setMessageType('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Verification email sent successfully! Please check your email inbox.');
        setMessageType('success');
      } else {
        // Handle specific error cases
        if (response.status === 401) {
          setMessage('Session expired. Redirecting to login...');
          setMessageType('error');
          setTimeout(() => {
            localStorage.removeItem('token');
            navigate('/login');
          }, 2000);
        } else if (response.status === 404) {
          setMessage('User not found. Redirecting to login...');
          setMessageType('error');
          setTimeout(() => {
            localStorage.removeItem('token');
            navigate('/login');
          }, 2000);
        } else {
          setMessage(data.message || 'Failed to send verification email. Please try again.');
          setMessageType('error');
        }
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setMessage('An error occurred. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyNow = () => {
    // Email doğrulama sayfasına yönlendir
    window.open('/verify-email', '_blank');
  };

  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-6 rounded-lg shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex items-start flex-1">
          <div className="flex-shrink-0">
            <FaExclamationTriangle className="h-6 w-6 text-yellow-500" />
          </div>
          <div className="ml-4 flex-1 text-left">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2 text-left">
              Email Address Not Verified
            </h3>
            <div className="text-sm text-yellow-700 text-left">
              <p className="text-left">
                You need to verify your email address <strong>{user?.email}</strong>. 
                Some features may be limited until verification is complete.
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="ml-4 text-yellow-400 hover:text-yellow-600 transition-colors p-1"
        >
          <FaTimes className="h-5 w-5" />
        </button>
      </div>
      
      {/* Message Display */}
      {message && (
        <div className={`mt-4 p-4 rounded-md ${
          messageType === 'success' 
            ? 'bg-green-50 text-green-700 border border-green-200' 
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          <div className="flex items-center">
            {messageType === 'success' ? (
              <FaCheck className="h-5 w-5 mr-3" />
            ) : (
              <FaExclamationTriangle className="h-5 w-5 mr-3" />
            )}
            <span className="text-sm font-medium">{message}</span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
        <button
          onClick={handleVerifyNow}
          className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-sm"
        >
          <FaCheck className="h-4 w-4 mr-2" />
          Verify Email
        </button>
        
        <button
          onClick={handleResendVerification}
          disabled={loading}
          className="inline-flex items-center px-6 py-3 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          {loading ? (
            <FaSpinner className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <FaEnvelope className="h-4 w-4 mr-2" />
          )}
          {loading ? 'Sending...' : 'Send Verification Email'}
        </button>
      </div>

      {/* Note */}
      <div className="mt-4 text-xs text-yellow-600 bg-yellow-100 p-3 rounded-md">
        <p>
          <strong>Note:</strong> If you don't receive the verification email, check your spam folder. 
          If the email doesn't arrive, use the "Send Verification Email" button above.
        </p>
      </div>
    </div>
  );
};

export default EmailVerificationWarning;
